import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "usair_cart_v1";

function loadInitialCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Cart item shape:
 * {
 *   product_code: string,  // _ComputedKey from Parts
 *   part_id: number|null,
 *   product_name: string,
 *   quantity: number,
 *   price: number,
 *   currency: "USD"|"CAD",
 *   hp?: number,
 *   category?: string,
 *   added_at: iso-string
 * }
 */
export function CartProvider({ children }) {
  const [items, setItems] = useState(loadInitialCart);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Persist on every change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch { /* ignore */ }
  }, [items]);

  const addItem = useCallback((item, qty = 1) => {
    setItems((prev) => {
      const key = `${item.product_code}|${item.currency || "USD"}|${item.discount || 0}|${item.kit_label || ""}`;
      const existing = prev.findIndex((p) => `${p.product_code}|${p.currency || "USD"}|${p.discount || 0}|${p.kit_label || ""}` === key);
      if (existing >= 0) {
        const next = [...prev];
        next[existing] = { ...next[existing], quantity: (Number(next[existing].quantity) || 0) + qty };
        return next;
      }
      return [
        ...prev,
        {
          product_code: item.product_code,
          part_id: item.part_id ?? null,
          product_name: item.product_name || item.product_code,
          quantity: qty,
          price: Number(item.price) || 0,
          currency: (item.currency || "USD").toUpperCase(),
          hp: item.hp ?? null,
          category: item.category || "",
          // Per-line discount as a fraction (0..1). Used for Starter-Kit bundles
          // where the buyer gets 10% off the kit lines only.
          discount: Math.max(0, Math.min(1, Number(item.discount) || 0)),
          kit_label: item.kit_label || "",
          added_at: new Date().toISOString(),
        },
      ];
    });
    setDrawerOpen(true);
  }, []);

  const updateQty = useCallback((product_code, currency, qty) => {
    setItems((prev) =>
      prev.map((p) =>
        p.product_code === product_code && (p.currency || "USD") === (currency || "USD")
          ? { ...p, quantity: Math.max(1, Number(qty) || 1) }
          : p
      )
    );
  }, []);

  const removeItem = useCallback((product_code, currency) => {
    setItems((prev) =>
      prev.filter(
        (p) => !(p.product_code === product_code && (p.currency || "USD") === (currency || "USD"))
      )
    );
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const count = useMemo(
    () => items.reduce((sum, it) => sum + (Number(it.quantity) || 0), 0),
    [items]
  );

  const subtotal = useMemo(
    () =>
      items.reduce(
        (sum, it) =>
          sum +
          (Number(it.quantity) || 0) *
            (Number(it.price) || 0) *
            (1 - Math.max(0, Math.min(1, Number(it.discount) || 0))),
        0
      ),
    [items]
  );

  const currency = items[0]?.currency || "USD";

  const value = useMemo(
    () => ({
      items, count, subtotal, currency,
      addItem, updateQty, removeItem, clear,
      drawerOpen, setDrawerOpen,
    }),
    [items, count, subtotal, currency, addItem, updateQty, removeItem, clear, drawerOpen]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
}

export function formatMoney(value, currency = "USD") {
  const n = Number(value) || 0;
  const prefix = currency === "CAD" ? "C$" : "$";
  return `${prefix}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
